/**
 * Filing bookings under "upcoming" or "past".
 *
 * Pulled out of the screen because the rule is a decision, not a rendering detail: which
 * group a trip lands in decides which actions it is offered, and a trip that is upcoming in
 * the heading while its buttons think it has departed is the bug this shape prevents.
 */

export interface GroupableTrip {
  trip: { departureAt: string };
}

export interface TripGroups<T> {
  upcoming: T[];
  past: T[];
}

/**
 * Splits once against a single `now`, rather than asking the clock per booking. A departure
 * passing midway through a render would otherwise file two trips on opposite sides of it.
 *
 * A departure exactly at `now` counts as upcoming: the bus has not left until it has, and a
 * passenger standing at the gate must not watch their ticket disappear into history.
 */
export function groupTrips<T extends GroupableTrip>(bookings: T[], now: number): TripGroups<T> {
  const upcoming: T[] = [];
  const past: T[] = [];

  for (const booking of bookings) {
    const departure = new Date(booking.trip.departureAt).getTime();
    // An unparseable date is filed as upcoming. Losing a trip into a collapsed section the
    // passenger may never open is worse than showing one that should not be there.
    if (Number.isNaN(departure) || departure >= now) upcoming.push(booking);
    else past.push(booking);
  }

  // Soonest first among what is still to come — the trip a passenger needs is the next one.
  upcoming.sort((a, b) => a.trip.departureAt.localeCompare(b.trip.departureAt));
  // Most recent first among what has happened, for the opposite reason.
  past.sort((a, b) => b.trip.departureAt.localeCompare(a.trip.departureAt));

  return { upcoming, past };
}
